
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function MarketReportsPage() {
  const [selectedCounty, setSelectedCounty] = useState('wayne');
  const [selectedTimeframe, setSelectedTimeframe] = useState('q3-2025');

  const counties = {
    'wayne': 'Wayne County',
    'oakland': 'Oakland County', 
    'macomb': 'Macomb County',
    'saginaw': 'Saginaw County',
    'kent': 'Kent County'
  };

  const timeframes = {
    'q3-2025': 'Q3 2025',
    'q2-2025': 'Q2 2025',
    'q1-2025': 'Q1 2025',
    'q4-2024': 'Q4 2024'
  };

  const marketData = {
    'wayne': {
      'q3-2025': {
        medianPrice: 185000,
        priceChange: 5.8,
        daysOnMarket: 28,
        inventoryMonths: 2.1,
        salesVolume: 3247,
        volumeChange: 12.3,
        pricePerSqFt: 142,
        activeListings: 1850
      }
    },
    'oakland': {
      'q3-2025': {
        medianPrice: 385000,
        priceChange: 4.2,
        daysOnMarket: 32,
        inventoryMonths: 2.8,
        salesVolume: 2156,
        volumeChange: 8.7,
        pricePerSqFt: 198,
        activeListings: 2240
      }
    },
    'macomb': {
      'q3-2025': {
        medianPrice: 245000,
        priceChange: 6.1,
        daysOnMarket: 25,
        inventoryMonths: 1.9,
        salesVolume: 1987,
        volumeChange: 15.2,
        pricePerSqFt: 156,
        activeListings: 1420
      }
    },
    'saginaw': {
      'q3-2025': {
        medianPrice: 125000,
        priceChange: 7.3,
        daysOnMarket: 35,
        inventoryMonths: 3.2,
        salesVolume: 892,
        volumeChange: 18.9,
        pricePerSqFt: 98,
        activeListings: 980
      }
    },
    'kent': {
      'q3-2025': {
        medianPrice: 295000,
        priceChange: 3.8,
        daysOnMarket: 29,
        inventoryMonths: 2.5,
        salesVolume: 1654,
        volumeChange: 6.4,
        pricePerSqFt: 175,
        activeListings: 1680
      }
    }
  };

  const currentData = marketData[selectedCounty as keyof typeof marketData]?.[selectedTimeframe as keyof typeof marketData.wayne] || marketData.wayne['q3-2025'];

  const marketTrends = [
    {
      title: 'First-Time Buyer Activity Surges',
      date: 'October 2025',
      description: 'MSHDA and FHA loan usage up 23% as rates stabilize below 6.5%',
      impact: 'positive',
      icon: 'ri-home-heart-line'
    },
    {
      title: 'Inventory Levels Normalizing',
      date: 'September 2025',
      description: 'Housing inventory reaches 2.3 months supply, approaching balanced market',
      impact: 'neutral',
      icon: 'ri-bar-chart-line'
    },
    {
      title: 'New Construction Completions Peak',
      date: 'August 2025',
      description: '1,850 new homes delivered across Metro Detroit in Q3',
      impact: 'positive',
      icon: 'ri-building-line'
    },
    {
      title: 'Interest Rate Volatility Continues',
      date: 'July 2025',
      description: 'Rates fluctuating between 6.2% - 6.8% based on economic indicators',
      impact: 'neutral',
      icon: 'ri-line-chart-line'
    }
  ];

  const neighborhoodSpotlight = [
    {
      name: 'Royal Oak',
      county: 'Oakland',
      medianPrice: 425000,
      priceChange: 8.2,
      highlight: 'Walkable downtown, excellent schools',
      image: 'https://public.readdy.ai/ai/img_res/582598522bf044893538e7efa10230f3.jpg'
    },
    {
      name: 'Plymouth',
      county: 'Wayne',
      medianPrice: 365000,
      priceChange: 6.7,
      highlight: 'Historic charm, family-friendly',
      image: 'https://readdy.ai/api/search-image?query=Plymouth%20Michigan%20historic%20downtown%20with%20charming%20architecture%2C%20family-friendly%20neighborhood%2C%20tree-lined%20residential%20streets&width=300&height=200&seq=neighborhood-2&orientation=landscape'
    },
    {
      name: 'Rochester Hills',
      county: 'Oakland',
      medianPrice: 485000,
      priceChange: 4.9,
      highlight: 'Top-rated schools, luxury amenities',
      image: 'https://readdy.ai/api/search-image?query=Rochester%20Hills%20Michigan%20upscale%20residential%20neighborhood%20with%20luxury%20homes%2C%20well-maintained%20landscaping%2C%20suburban%20setting&width=300&height=200&seq=neighborhood-3&orientation=landscape'
    },
    {
      name: 'Shelby Township',
      county: 'Macomb',
      medianPrice: 285000,
      priceChange: 7.1,
      highlight: 'New developments, growing community',
      image: 'https://readdy.ai/api/search-image?query=Shelby%20Township%20Michigan%20new%20residential%20development%20with%20modern%20homes%2C%20growing%20suburban%20community%2C%20family%20neighborhoods&width=300&height=200&seq=neighborhood-4&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-black mb-4">
              Metro Detroit Market Reports
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay informed with the latest real estate market data, trends, and insights across Wayne, Oakland, Macomb, Saginaw, and Kent Counties.
            </p>
          </div>

          {/* Market Selector */}
          <div className="bg-black text-white rounded-lg p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-bold mb-3">Select County</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(counties).map(([key, name]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedCounty(key)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        selectedCounty === key
                          ? 'bg-yellow-500 text-black'
                          : 'bg-white text-black hover:bg-gray-100'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-bold mb-3">Select Time Period</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(timeframes).map(([key, name]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedTimeframe(key)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        selectedTimeframe === key
                          ? 'bg-yellow-500 text-black'
                          : 'bg-white text-black hover:bg-gray-100'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Dashboard */}
          <div className="bg-white border-2 border-yellow-500 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-black mb-6 text-center">
              {counties[selectedCounty as keyof typeof counties]} - {timeframes[selectedTimeframe as keyof typeof timeframes]} Market Snapshot
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  ${currentData.medianPrice.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 mb-1">Median Sale Price</div>
                <div className={`text-sm font-medium ${currentData.priceChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {currentData.priceChange > 0 ? '+' : ''}{currentData.priceChange}% YoY
                </div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {currentData.daysOnMarket}
                </div>
                <div className="text-sm text-gray-600 mb-1">Avg Days on Market</div>
                <div className="text-sm text-gray-500">Fast-moving market</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {currentData.inventoryMonths}
                </div>
                <div className="text-sm text-gray-600 mb-1">Months of Inventory</div>
                <div className="text-sm text-gray-500">
                  {currentData.inventoryMonths < 2 ? 'Seller\'s Market' : currentData.inventoryMonths > 4 ? 'Buyer\'s Market' : 'Balanced Market'}
                </div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {currentData.salesVolume.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 mb-1">Homes Sold</div>
                <div className={`text-sm font-medium ${currentData.volumeChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {currentData.volumeChange > 0 ? '+' : ''}{currentData.volumeChange}% vs Q2
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 mb-2">
                  ${currentData.pricePerSqFt}
                </div>
                <div className="text-sm text-gray-600">Price per Square Foot</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 mb-2">
                  {currentData.activeListings.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Active Listings</div>
              </div>
            </div>
          </div>

          {/* Market Trends */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-black mb-6 text-center">Latest Market Trends</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {marketTrends.map((trend, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-yellow-500 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      trend.impact === 'positive' ? 'bg-green-100' : 
                      trend.impact === 'negative' ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      <i className={`${trend.icon} text-xl ${
                        trend.impact === 'positive' ? 'text-green-600' : 
                        trend.impact === 'negative' ? 'text-red-600' : 'text-gray-600'
                      }`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-black">{trend.title}</h3>
                        <span className="text-sm text-gray-500">{trend.date}</span>
                      </div>
                      <p className="text-gray-600 text-sm">{trend.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Neighborhood Spotlight */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-black mb-6 text-center">Neighborhood Spotlight</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {neighborhoodSpotlight.map((neighborhood, index) => (
                <div key={index} className="bg-white border-2 border-yellow-500 rounded-lg overflow-hidden hover:border-yellow-400 transition-colors">
                  <img 
                    src={neighborhood.image}
                    alt={neighborhood.name}
                    className="w-full h-40 object-cover object-top"
                  />
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-black">{neighborhood.name}</h3>
                      <span className="text-xs text-gray-500">{neighborhood.county} County</span>
                    </div>
                    <div className="text-lg font-bold text-yellow-600 mb-1">
                      ${neighborhood.medianPrice.toLocaleString()}
                    </div>
                    <div className="text-sm text-green-600 font-medium mb-2">
                      +{neighborhood.priceChange}% YoY
                    </div>
                    <p className="text-gray-600 text-xs">{neighborhood.highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Market Forecast */}
          <div className="bg-black text-white rounded-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-center mb-6">Q4 2025 & 2026 Market Forecast</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-arrow-up-line text-2xl text-black"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Price Appreciation</h3>
                <p className="text-gray-300 text-sm">
                  Continued moderate growth of 3-5% annually through 2026, driven by steady demand and controlled inventory.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-scales-line text-2xl text-black"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Market Balance</h3>
                <p className="text-gray-300 text-sm">
                  Transition toward balanced market conditions as inventory normalizes and buyer competition moderates.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-2-line text-2xl text-black"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">New Construction</h3>
                <p className="text-gray-300 text-sm">
                  22,000+ new homes planned for 2026, focusing on affordable and sustainable housing options.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white border-2 border-yellow-500 rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-black mb-4">Get Personalized Market Analysis</h3>
              <p className="text-gray-600 mb-6">
                Want detailed market data for your specific neighborhood or property type? Get a customized market report tailored to your needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-yellow-500 text-black px-8 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-chart-line mr-2"></i>
                  Request Custom Report
                </button>
                <button className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
